import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { hospitalAdminApi, type GoogleAdsIntegrationConfig } from "@/api/hospitalAdmin";
import { superAdminApi } from "@/api/superAdmin";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function apiErrorMessage(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { message?: string } }; message?: string };
  return err.response?.data?.message || err.message || fallback;
}

export default function GoogleIntegrationPage() {
  const qc = useQueryClient();
  const { isChainAdmin, user, activeHospitalId } = useAuth();
  const [hospitalId, setHospitalId] = useState("");
  const [isEnabled, setIsEnabled] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [loginCustomerId, setLoginCustomerId] = useState("");
  const [googleCloudProjectId, setGoogleCloudProjectId] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [conversionLead, setConversionLead] = useState("");
  const [conversionAppointmentBooked, setConversionAppointmentBooked] = useState("");
  const [conversionOpdVisited, setConversionOpdVisited] = useState("");
  const [conversionSurgeryBooked, setConversionSurgeryBooked] = useState("");
  const [conversionPurchase, setConversionPurchase] = useState("");
  const [testEventName, setTestEventName] = useState("Lead");
  const [testLeadId, setTestLeadId] = useState("");

  const { data: superHospitals = [] } = useQuery({
    queryKey: ["super-admin-hospitals-google"],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: !isChainAdmin,
  });

  const hospitals = isChainAdmin ? user?.hospitals ?? [] : superHospitals;

  useEffect(() => {
    if (!hospitalId && activeHospitalId) setHospitalId(activeHospitalId);
  }, [activeHospitalId, hospitalId]);

  const { data: cfg } = useQuery<GoogleAdsIntegrationConfig>({
    queryKey: ["google-integration-config", hospitalId],
    queryFn: () => hospitalAdminApi.getGoogleIntegrationConfig(hospitalId),
    enabled: !!hospitalId,
  });

  useEffect(() => {
    if (!cfg) return;
    setIsEnabled(cfg.isEnabled ?? false);
    setCustomerId(cfg.customerId ?? "");
    setLoginCustomerId(cfg.loginCustomerId ?? "");
    setGoogleCloudProjectId(cfg.googleCloudProjectId ?? "");
    setClientId(cfg.clientId ?? "");
    setClientSecret("");
    setRefreshToken("");
    setConversionLead(cfg.conversionLead ?? "");
    setConversionAppointmentBooked(cfg.conversionAppointmentBooked ?? "");
    setConversionOpdVisited(cfg.conversionOpdVisited ?? "");
    setConversionSurgeryBooked(cfg.conversionSurgeryBooked ?? "");
    setConversionPurchase(cfg.conversionPurchase ?? "");
  }, [cfg, hospitalId]);

  const saveMutation = useMutation({
    mutationFn: () =>
      hospitalAdminApi.saveGoogleIntegrationConfig({
        hospitalId,
        isEnabled,
        customerId,
        loginCustomerId,
        googleCloudProjectId,
        clientId,
        clientSecret: clientSecret.trim() ? clientSecret.trim() : undefined,
        refreshToken: refreshToken.trim() ? refreshToken.trim() : undefined,
        conversionLead,
        conversionAppointmentBooked,
        conversionOpdVisited,
        conversionSurgeryBooked,
        conversionPurchase,
      } as any),
    onSuccess: () => {
      toast.success("Google Ads integration saved");
      setClientSecret("");
      setRefreshToken("");
      qc.invalidateQueries({ queryKey: ["google-integration-config", hospitalId] });
    },
    onError: (e: Error) => toast.error(apiErrorMessage(e, "Failed to save")),
  });

  const testConversionMutation = useMutation({
    mutationFn: () =>
      hospitalAdminApi.sendGoogleTestConversion({
        hospitalId,
        eventName: testEventName,
        ...(testLeadId.trim() ? { leadId: testLeadId.trim() } : {}),
      }),
    onSuccess: (data) => {
      if (data.ok) {
        toast.success(
          data.validateOnly
            ? `Credentials validated for ${data.eventName}. This does not record a conversion in Google Ads — paste a lead ID with gclid for a real upload.`
            : `Test conversion sent for lead (${data.eventName}). Allow a few hours, then check Google Ads → Conversions → Uploads.`
        );
      } else {
        toast.error(data.error || `Test conversion failed (${data.eventName})`);
      }
      qc.invalidateQueries({ queryKey: ["google-event-logs"] });
    },
    onError: (e: Error) => toast.error(apiErrorMessage(e, "Test conversion failed")),
  });

  const credentialsReady = cfg?.hasCredentials;

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      <div>
        <h1 className="text-2xl font-semibold">Google Ads Integration</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Configure per-hospital offline conversion uploads via Google Data Manager API.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Hospital</CardTitle>
        </CardHeader>
        <CardContent>
          <Label>Select hospital</Label>
          <select
            className="mt-2 w-full border rounded-md p-2"
            value={hospitalId}
            onChange={(e) => setHospitalId(e.target.value)}
          >
            <option value="">Select…</option>
            {hospitals.map((h: any) => (
              <option key={h.id} value={h.id}>
                {h.name}
              </option>
            ))}
          </select>
        </CardContent>
      </Card>

      {!!hospitalId && (
        <Card>
          <CardHeader>
            <CardTitle>Credentials & conversion actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-2">
              <Checkbox id="google-enabled" checked={isEnabled} onCheckedChange={(v) => setIsEnabled(!!v)} />
              <div className="space-y-1">
                <Label htmlFor="google-enabled">Enable Google uploads</Label>
                <p className="text-xs text-muted-foreground">
                  If disabled, events are not sent (queue stays failed/pending).
                </p>
              </div>
            </div>

            {cfg && !credentialsReady && (
              <p className="text-sm text-amber-700 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                OAuth is not fully configured. Enter <strong>Client ID</strong>, <strong>Client secret</strong>,{" "}
                <strong>Refresh token</strong> (with Data Manager scope), and <strong>Google Cloud project ID</strong>,
                then Save. Leaving secret fields blank on Save keeps existing values.
                {cfg.usingEnvFallback ? " Server env credentials may still apply." : ""}
              </p>
            )}

            {cfg?.hasCredentials && (
              <p className="text-sm text-green-700 rounded-md border border-green-200 bg-green-50 px-3 py-2">
                Data Manager credentials are saved for this hospital.
              </p>
            )}

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Customer ID</Label>
                <Input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="6012003360" />
                <p className="text-xs text-muted-foreground">
                  Google Ads account ID (no dashes). Conversion actions must exist in this account.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Google Cloud project ID</Label>
                <Input
                  value={googleCloudProjectId}
                  onChange={(e) => setGoogleCloudProjectId(e.target.value)}
                  placeholder="my-gcp-project-id"
                />
                <p className="text-xs text-muted-foreground">
                  GCP project with Data Manager API enabled (same project as your OAuth client).
                </p>
              </div>
              <div className="space-y-2">
                <Label>Login customer ID (MCC, optional)</Label>
                <Input
                  value={loginCustomerId}
                  onChange={(e) => setLoginCustomerId(e.target.value)}
                  placeholder=""
                />
                <p className="text-xs text-muted-foreground">
                  Only if uploading via a manager (MCC) account.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Client ID</Label>
                <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="OAuth client id" />
              </div>
              <div className="space-y-2">
                <Label>Client Secret</Label>
                <Input value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="(leave blank to keep existing)" />
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Refresh Token</Label>
                <Input value={refreshToken} onChange={(e) => setRefreshToken(e.target.value)} placeholder="(leave blank to keep existing)" />
                <p className="text-xs text-muted-foreground">
                  Must include scope <code className="text-[11px]">https://www.googleapis.com/auth/datamanager</code>.
                  Re-authorize if uploads fail with invalid_scope.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Conversion Action ID — Lead</Label>
                <Input value={conversionLead} onChange={(e) => setConversionLead(e.target.value)} placeholder="123456789" />
              </div>
              <div className="space-y-2">
                <Label>Appointment_Booked</Label>
                <Input value={conversionAppointmentBooked} onChange={(e) => setConversionAppointmentBooked(e.target.value)} placeholder="123456789" />
              </div>
              <div className="space-y-2">
                <Label>OPD_Visited</Label>
                <Input value={conversionOpdVisited} onChange={(e) => setConversionOpdVisited(e.target.value)} placeholder="123456789" />
              </div>
              <div className="space-y-2">
                <Label>Surgery_Booked</Label>
                <Input value={conversionSurgeryBooked} onChange={(e) => setConversionSurgeryBooked(e.target.value)} placeholder="123456789" />
              </div>
              <div className="space-y-2">
                <Label>Purchase</Label>
                <Input value={conversionPurchase} onChange={(e) => setConversionPurchase(e.target.value)} placeholder="123456789" />
              </div>
            </div>

            <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
              <div>
                <p className="text-sm font-medium">Test conversion upload</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Without a lead ID, this only validates credentials (nothing is recorded in Google Ads).
                  Paste a lead ID with a real gclid from a Google ad click to send an actual conversion upload.
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Event to test</Label>
                  <Select value={testEventName} onValueChange={setTestEventName}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Lead">Lead</SelectItem>
                      <SelectItem value="Appointment_Booked">Appointment_Booked</SelectItem>
                      <SelectItem value="OPD_Visited">OPD_Visited</SelectItem>
                      <SelectItem value="Surgery_Booked">Surgery_Booked</SelectItem>
                      <SelectItem value="Purchase">Purchase</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Lead ID (optional)</Label>
                  <Input
                    value={testLeadId}
                    onChange={(e) => setTestLeadId(e.target.value)}
                    placeholder="Real lead UUID — sends actual conversion"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={!hospitalId || testConversionMutation.isPending}
                onClick={() => testConversionMutation.mutate()}
              >
                {testConversionMutation.isPending ? "Testing…" : "Send test conversion"}
              </Button>
              <Button disabled={saveMutation.isPending} onClick={() => saveMutation.mutate()}>
                {saveMutation.isPending ? "Saving…" : "Save"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

